/**
 * CreditStore — modale MUI d'achat de packs de crédits Life.
 * Charte alignée sur le panneau Abonnement (UserOptionCard/Abonnement.js).
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import StarsRoundedIcon from '@mui/icons-material/StarsRounded'

import { selectPacks } from '../../reduxLib/slices/billingSlice'
import { loadPacks, startPackCheckout } from './billingThunks'

const NAVY = '#001B42'
const ACCENT = '#2563EB'
const BORDER = '#E5E7EB'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

function formatCredits(n) {
  return new Intl.NumberFormat('fr-FR').format(n)
}

function formatPrice(eur) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(Number(eur))
}

function unitPrice(pack) {
  return Number(pack.price_eur) / Number(pack.credits)
}

export default function CreditStore({ open, onClose }) {
  const dispatch = useDispatch()
  const { t } = useTranslation('layout')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const packs = useSelector(selectPacks)
  const [pending, setPending] = useState(null)
  // État de chargement local : permet de distinguer "fetch en cours" (spinner)
  // de "fetch terminé mais 0 résultats" (message explicite). Sans ça la
  // modale tournait à l'infini si l'API renvoyait une liste vide (cas où
  // aucun CreditPack n'est is_active=True côté backend).
  const [loadState, setLoadState] = useState('idle') // 'idle' | 'loading' | 'ready' | 'error'

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadState('loading')
    dispatch(loadPacks())
      .then(() => {
        if (!cancelled) setLoadState('ready')
      })
      .catch(() => {
        if (!cancelled) setLoadState('error')
      })
    return () => {
      cancelled = true
    }
  }, [open, dispatch])

  async function handleBuy(packCode) {
    setPending(packCode)
    const ok = await dispatch(startPackCheckout(packCode))
    if (!ok) setPending(null)
  }

  // Pack avec le meilleur tarif unitaire → badge "Meilleure offre".
  const bestPackCode = useMemo(() => {
    if (!packs || packs.length === 0) return null
    return packs.reduce(
      (best, p) => (!best || unitPrice(p) < unitPrice(best) ? p : best),
      null,
    )?.code
  }, [packs])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          bgcolor: '#F7F9FC',
        },
      }}
    >
      {/* Header — gradient navy identique au panneau Abonnement */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #0A2A66 100%)`,
          color: 'white',
          px: isMobile ? 2 : 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('settings.credits.store.title', 'Acheter des crédits Life')}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ ml: 'auto', color: 'white' }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: isMobile ? 2 : 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t(
            'settings.credits.store.lead',
            'Les crédits achetés sont permanents et viennent compléter le quota mensuel inclus dans votre abonnement.',
          )}
        </Typography>

        {loadState === 'loading' ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : !packs || packs.length === 0 ? (
          <Box
            sx={{
              border: `1px dashed ${BORDER}`,
              borderRadius: '8px',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: 14, color: NAVY, fontWeight: 600 }}>
              {t(
                'settings.credits.store.unavailableTitle',
                'Aucun pack disponible pour le moment',
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t(
                'settings.credits.store.unavailableHint',
                'La boutique de crédits sera bientôt disponible. Réessayez dans quelques minutes.',
              )}
            </Typography>
          </Box>
        ) : (
          <Box
            display="grid"
            gap={2}
            sx={{
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: 'repeat(4, 1fr)',
              },
            }}
          >
            {packs.map((pack) => {
              const isBest = pack.code === bestPackCode
              const isPending = pending === pack.code
              return (
                <Card
                  key={pack.code}
                  elevation={0}
                  sx={{
                    position: 'relative',
                    // LAP-733: allow the "Meilleure offre" Chip (top: -12) to
                    // overflow the Card; MUI's default overflow:hidden was
                    // clipping the top of the badge text.
                    overflow: 'visible',
                    border: isBest ? `2px solid ${NAVY}` : `1px solid ${BORDER}`,
                    borderRadius: '12px',
                    transition: 'transform .15s ease, box-shadow .15s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 20px rgba(0, 27, 66, 0.08)',
                    },
                  }}
                >
                  {isBest && (
                    <Chip
                      size="small"
                      label={t(
                        'settings.credits.store.bestOffer',
                        'Meilleure offre',
                      )}
                      sx={{
                        position: 'absolute',
                        // Sit slightly above the card; height bumped to 24 so
                        // the uppercase text isn't vertically clipped.
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: NAVY,
                        color: 'white',
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        height: 24,
                        lineHeight: '24px',
                        zIndex: 1,
                        '& .MuiChip-label': { px: 1.25, py: 0 },
                      }}
                    />
                  )}
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      pt: isBest ? 3 : 2.5,
                    }}
                  >
                    <StarsRoundedIcon
                      sx={{ fontSize: 32, color: NAVY, opacity: 0.85 }}
                    />
                    <Typography
                      sx={{ fontSize: 28, fontWeight: 700, mt: 1, color: NAVY, lineHeight: 1 }}
                    >
                      {formatCredits(pack.credits)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.credits.store.credits', 'crédits')}
                    </Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, mt: 1.5, color: NAVY }}>
                      {formatPrice(pack.price_eur)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                      {t('settings.credits.store.unitPrice', 'soit {{p}} / crédit', {
                        p: formatPrice(unitPrice(pack)),
                      })}
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleBuy(pack.code)}
                      disabled={isPending}
                      sx={{
                        mt: 2.5,
                        bgcolor: ACCENT,
                        '&:hover': { bgcolor: '#1D4ED8' },
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {isPending
                        ? t('settings.credits.store.redirecting', 'Redirection…')
                        : t('settings.credits.store.buy', 'Acheter')}
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
          {t(
            'settings.credits.store.stripeNotice',
            'Paiement sécurisé par Stripe. Vous serez redirigé pour finaliser l\u2019achat.',
          )}
        </Typography>
      </Box>
    </Dialog>
  )
}
